/* GOS · Collection finder: instant filters, shortcuts, sort and shareable URLs (sections/gos-collection.liquid).
   Within a group, options are OR'ed (Beginner or Hobbyist); the "feature" group is AND'ed; groups are AND'ed.
   URL params: level, type, budget, size, feature (comma-separated slugs) and sort. */
(() => {
  if (customElements.get('gos-collection')) return;

  const GROUPS = ['level', 'type', 'budget', 'size', 'feature'];
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 990px)');

  class GosCollection extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-gos-list]');
      this.items = [...this.querySelectorAll('[data-gos-item]')];
      this.panel = this.querySelector('[data-gos-panel]');
      if (!this.list || !this.panel || this.items.length < 2) return;

      this.toolbar = this.querySelector('[data-gos-toolbar]');
      this.guide = this.querySelector('[data-gos-guide]');
      this.toggle = this.querySelector('[data-gos-toggle]');
      this.sortSelect = this.querySelector('[data-gos-sort]');
      this.statusEl = this.querySelector('[data-gos-status]');
      this.pillsEl = this.querySelector('[data-gos-pills]');
      this.badge = this.querySelector('[data-gos-active-count]');
      this.emptyEl = this.querySelector('[data-gos-empty]');
      this.results = this.querySelector('.gos-cf__results');
      this.applyBtn = this.querySelector('[data-gos-apply]');
      this.intents = [...this.querySelectorAll('[data-gos-intent]')];
      this.inputs = [...this.panel.querySelectorAll('input[type="checkbox"]')];
      this.guideMore = this.querySelector('[data-gos-guide-more]');
      this.headingEl = this.querySelector('[data-gos-heading]');
      this.introEl = this.querySelector('[data-gos-intro]');
      this.landings = [...this.querySelectorAll('template[data-gos-landing]')];
      // The inline script in the section may already have swapped in landing copy; it keeps the originals in data-default.
      this.defaultCopy = {
        heading: this.headingEl?.dataset.default ?? this.headingEl?.textContent,
        intro: this.introEl?.dataset.default ?? this.introEl?.innerHTML,
      };

      this.data = this.items.map((el) => {
        const tokens = {};
        for (const g of GROUPS) tokens[g] = (el.dataset[`f${g[0].toUpperCase()}${g.slice(1)}`] || '').split(' ').filter(Boolean);
        return { el, tokens, index: Number(el.dataset.index), price: Number(el.dataset.price), available: el.dataset.available === 'true' };
      });
      this.logic = {};
      for (const fs of this.panel.querySelectorAll('[data-gos-group]')) this.logic[fs.dataset.gosGroup] = fs.dataset.logic;

      this.pruneOptions();
      this.state = this.readUrl();
      this.syncInputs();

      this.panel.addEventListener('submit', (e) => e.preventDefault());
      this.panel.addEventListener('change', (e) => {
        if (!e.target.matches('input[type="checkbox"]')) return;
        this.state = this.readInputs();
        this.update();
      });
      this.querySelectorAll('[data-gos-clear]').forEach((b) => b.addEventListener('click', () => this.setState(this.emptyState())));
      this.intents.forEach((b) => b.addEventListener('click', () => this.onIntent(b)));
      this.sortSelect?.addEventListener('change', () => {
        this.state.sort = this.sortSelect.value;
        this.update();
      });
      this.pillsEl?.addEventListener('click', (e) => {
        const pill = e.target.closest('[data-group]');
        if (!pill) return;
        this.state[pill.dataset.group] = this.state[pill.dataset.group].filter((v) => v !== pill.dataset.value);
        this.syncInputs();
        this.update();
        (this.pillsEl.querySelector('.gos-cf__pill') || this.statusEl).focus?.();
      });
      this.toggle?.addEventListener('click', () => this.setPanel(this.toggle.getAttribute('aria-expanded') !== 'true'));
      this.applyBtn?.addEventListener('click', () => {
        this.setPanel(false);
        this.scrollToResults(true);
      });
      this.panel.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !desktop.matches) {
          this.setPanel(false);
          this.toggle?.focus();
        }
      });
      desktop.addEventListener('change', () => this.setPanel(false));
      this.guideMore?.addEventListener('click', () => this.setGuide(this.guideMore.getAttribute('aria-expanded') !== 'true'));

      if (this.toolbar) this.toolbar.hidden = false;
      this.setPanel(false);
      this.update(false);
    }

    emptyState() {
      const s = { sort: this.state?.sort || 'featured' };
      for (const g of GROUPS) s[g] = [];
      return s;
    }

    /* Hide options no product has, options every product has (they filter nothing) and groups left with nothing to choose. */
    pruneOptions() {
      const total = this.data.length;
      for (const fs of this.panel.querySelectorAll('[data-gos-group]')) {
        const g = fs.dataset.gosGroup;
        let visible = 0;
        for (const input of fs.querySelectorAll('input')) {
          const n = this.data.filter((d) => d.tokens[g].includes(input.value)).length;
          const useless = n === 0 || n === total;
          input.closest('.gos-cf__opt').hidden = useless;
          input.disabled = useless;
          if (useless) input.dataset.pruned = 'true';
          if (!useless) visible += 1;
        }
        fs.hidden = fs.dataset.logic === 'and' ? visible === 0 : visible < 2;
      }
      this.hasFilters = [...this.panel.querySelectorAll('[data-gos-group]')].some((fs) => !fs.hidden);
      this.classList.toggle('gos-cf--nofilters', !this.hasFilters);
      if (this.toggle) this.toggle.hidden = !this.hasFilters;
      let shown = 0;
      for (const b of this.intents) {
        const [g, v] = b.dataset.gosIntent.split(':');
        const n = this.data.filter((d) => d.tokens[g]?.includes(v)).length;
        b.closest('[data-gos-intent-item]').hidden = n === 0;
        b.querySelector('[data-gos-intent-count]').textContent = `${n} ${n === 1 ? 'match' : 'matches'}`;
        if (n) shown += 1;
      }
      if (this.guide) this.guide.hidden = shown < 2;
    }

    readUrl() {
      const params = new URLSearchParams(location.search);
      const s = this.emptyState();
      for (const g of GROUPS) {
        const allowed = this.inputs.filter((i) => i.name === g && !i.dataset.pruned).map((i) => i.value);
        s[g] = (params.get(g) || '').split(',').filter((v) => allowed.includes(v));
      }
      const sort = params.get('sort');
      if (['featured', 'price-asc', 'price-desc'].includes(sort)) s.sort = sort;
      return s;
    }

    readInputs() {
      const s = this.emptyState();
      for (const i of this.inputs) if (i.checked) s[i.name].push(i.value);
      return s;
    }

    syncInputs() {
      for (const i of this.inputs) i.checked = this.state[i.name].includes(i.value);
      if (this.sortSelect) this.sortSelect.value = this.state.sort;
    }

    setState(s) {
      this.state = s;
      this.syncInputs();
      this.update();
    }

    onIntent(button) {
      const [g, v] = button.dataset.gosIntent.split(':');
      const already = button.getAttribute('aria-pressed') === 'true';
      const s = this.emptyState();
      if (!already) s[g] = [v];
      this.setState(s);
      if (!already) this.scrollToResults(false);
    }

    matches(d, state, skipGroup) {
      for (const g of GROUPS) {
        if (g === skipGroup) continue;
        const sel = state[g];
        if (!sel.length) continue;
        const ok = this.logic[g] === 'and' ? sel.every((v) => d.tokens[g].includes(v)) : sel.some((v) => d.tokens[g].includes(v));
        if (!ok) return false;
      }
      return true;
    }

    update(writeUrl = true) {
      const s = this.state;
      let shown = 0;
      for (const d of this.data) {
        const ok = this.matches(d, s);
        d.el.hidden = !ok;
        if (ok) shown += 1;
      }

      // Live counts: what each option would show if ticked, given the other choices.
      for (const i of this.inputs) {
        if (i.dataset.pruned) continue;
        const g = i.name;
        let n;
        if (this.logic[g] === 'and') {
          const next = { ...s, [g]: [...new Set([...s[g], i.value])] };
          n = this.data.filter((d) => this.matches(d, next)).length;
        } else {
          n = this.data.filter((d) => this.matches(d, s, g) && d.tokens[g].includes(i.value)).length;
        }
        const opt = i.closest('.gos-cf__opt');
        opt.querySelector('[data-gos-count]').textContent = n;
        const empty = n === 0 && !i.checked;
        opt.classList.toggle('is-empty', empty);
        i.disabled = empty;
      }

      this.sort();

      const active = GROUPS.reduce((n, g) => n + s[g].length, 0);
      const total = this.data.length;
      this.statusEl.innerHTML = active ? `Showing <strong>${shown}</strong> of ${total}` : `<strong>${total}</strong> products`;
      if (this.badge) {
        this.badge.hidden = !active;
        this.badge.textContent = active;
      }
      this.querySelectorAll('.gos-cf__clear').forEach((b) => (b.hidden = !active));
      if (this.emptyEl) this.emptyEl.hidden = shown > 0;
      if (this.applyBtn) this.applyBtn.textContent = `Show ${shown} ${shown === 1 ? 'result' : 'results'}`;

      for (const b of this.intents) {
        const [g, v] = b.dataset.gosIntent.split(':');
        b.setAttribute('aria-pressed', String(active === 1 && s[g]?.length === 1 && s[g][0] === v));
      }
      this.renderPills();
      this.applyLanding(active);
      if (writeUrl) this.writeUrl();
    }

    /* Heading and intro follow a single active filter that has landing copy; shortcuts unfold once no filter is left. */
    applyLanding(active) {
      if (this.guideMore) {
        this.guideMore.hidden = !active;
        if (!active) this.setGuide(true);
      }
      if (!this.headingEl) return;
      let copy = this.defaultCopy;
      if (active === 1) {
        const g = GROUPS.find((k) => this.state[k].length);
        const tpl = this.landings.find((t) => t.dataset.gosLanding === `${g}:${this.state[g][0]}`);
        if (tpl) copy = { heading: tpl.dataset.heading, intro: tpl.innerHTML };
      }
      if (this.headingEl.textContent !== copy.heading) this.headingEl.textContent = copy.heading;
      if (this.introEl && this.introEl.innerHTML !== copy.intro) {
        this.introEl.innerHTML = copy.intro;
        this.introEl.hidden = !copy.intro.trim();
      }
    }

    setGuide(open) {
      if (!this.guide || !this.guideMore) return;
      this.guide.toggleAttribute('data-collapsed', !open);
      this.guideMore.setAttribute('aria-expanded', String(open));
      this.guideMore.textContent = open ? 'Hide shortcuts' : 'Show all shortcuts';
    }

    sort() {
      const mode = this.state.sort;
      const sorted = [...this.data].sort((a, b) => {
        if (mode === 'price-asc') return a.price - b.price || a.index - b.index;
        if (mode === 'price-desc') return b.price - a.price || a.index - b.index;
        // Featured: collection order, sold-out products last.
        return Number(b.available) - Number(a.available) || a.index - b.index;
      });
      const current = [...this.list.children].filter((el) => el.matches('[data-gos-item]'));
      if (sorted.every((d, i) => d.el === current[i])) return;
      const frag = document.createDocumentFragment();
      for (const d of sorted) frag.append(d.el);
      this.list.append(frag);
    }

    renderPills() {
      if (!this.pillsEl) return;
      this.pillsEl.replaceChildren();
      for (const i of this.inputs) {
        if (!i.checked) continue;
        const label = i.closest('.gos-cf__opt').querySelector('.gos-cf__opt-label').textContent.trim();
        const li = document.createElement('li');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'gos-cf__pill';
        btn.dataset.group = i.name;
        btn.dataset.value = i.value;
        btn.setAttribute('aria-label', `Remove filter: ${label}`);
        btn.append(label);
        const x = document.createElement('span');
        x.setAttribute('aria-hidden', 'true');
        x.textContent = '×';
        btn.append(x);
        li.append(btn);
        this.pillsEl.append(li);
      }
    }

    writeUrl() {
      const url = new URL(location.href);
      for (const g of GROUPS) {
        if (this.state[g].length) url.searchParams.set(g, this.state[g].join(','));
        else url.searchParams.delete(g);
      }
      if (this.state.sort !== 'featured') url.searchParams.set('sort', this.state.sort);
      else url.searchParams.delete('sort');
      history.replaceState(history.state, '', url);
    }

    setPanel(open) {
      if (!this.hasFilters) {
        this.panel.hidden = true;
        return;
      }
      if (desktop.matches) {
        this.panel.hidden = false;
        this.toggle?.setAttribute('aria-expanded', 'false');
        return;
      }
      this.panel.hidden = !open;
      this.toggle?.setAttribute('aria-expanded', String(open));
      if (open) this.panel.querySelector('input:not(:disabled)')?.focus({ preventScroll: true });
    }

    scrollToResults(focus) {
      const target = this.toolbar && !this.toolbar.hidden ? this.toolbar : this.results;
      const top = target.getBoundingClientRect().top;
      if (top < 0 || top > window.innerHeight * 0.6) {
        target.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
      }
      if (focus) this.results.focus({ preventScroll: true });
    }
  }

  customElements.define('gos-collection', GosCollection);
})();
